import React, { useState } from "react";
import {
  Typography,
  TextField,
  Box,
  Button,
  IconButton,
  Chip,
  Tooltip,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import InfoPopover from "../../../../utils/informationButton";
import { AddBox, Delete } from "@material-ui/icons";
import LockIcon from "@material-ui/icons/Lock";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import CancelIcon from "@material-ui/icons/Cancel";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const PickerResponseComponent = (props) => {
  const { t } = useTranslation();
  const [addingChoiceIdx, setAddingChoiceIdx] = useState(null);
  const [newChoiceValue, setNewChoiceValue] = useState("");

  const responsesPicker = props.state.formDatas.responsesPicker || [""];
  const pickerChoices = props.state.formDatas.pickerChoices || [];

  // Returns all ordered items for a column (correct answer + distractors, in stored order)
  const getAllItems = (colIdx) => {
    const correctAnswer = responsesPicker[colIdx] || "";
    const choiceValue = pickerChoices[colIdx]?.value || "";
    if (!choiceValue) return correctAnswer ? [correctAnswer] : [];
    return choiceValue.split(";").filter((v) => v !== "");
  };

  const getDistractors = (colIdx) => {
    const correctAnswer = responsesPicker[colIdx] || "";
    return getAllItems(colIdx).filter((v) => v !== correctAnswer);
  };

  // Correct answer change: preserve distractors, rebuild choice value
  const handleCorrectAnswerChange = (colIdx, newAnswer) => {
    const newResponses = [...responsesPicker];
    newResponses[colIdx] = newAnswer;
    const newStr = newResponses.join(";");

    const oldCorrect = responsesPicker[colIdx] || "";
    const oldItems = getAllItems(colIdx);
    // Keep distractors (drop old correct, drop new answer if already present)
    const distractors = oldItems.filter(
      (v) => v !== oldCorrect && v !== newAnswer
    );
    const newChoiceStr = newAnswer
      ? [newAnswer, ...distractors].join(";")
      : distractors.join(";");

    props.handleResponsesPickerTextChange({ target: { value: newStr } }, () => {
      props.updatePickerChoice(
        { target: { id: `picker-response-${colIdx}`, value: newChoiceStr } },
        colIdx
      );
    });
  };

  const handleAddColumn = () => {
    const newStr = [...responsesPicker, ""].join(";");
    props.handleResponsesPickerTextChange({ target: { value: newStr } });
  };

  const handleRemoveColumn = (colIdx) => {
    const newResponses = responsesPicker.filter((_, i) => i !== colIdx);
    const newStr = newResponses.length > 0 ? newResponses.join(";") : "";
    props.handleResponsesPickerTextChange({ target: { value: newStr } });
  };

  const handleAddDistractor = (colIdx) => {
    const val = newChoiceValue.trim();
    setAddingChoiceIdx(null);
    setNewChoiceValue("");
    if (!val) return;

    const correctAnswer = responsesPicker[colIdx] || "";
    const items = getAllItems(colIdx);
    if (items.includes(val) || val === correctAnswer) return;

    const newParts = [...items, val].filter(Boolean);
    props.updatePickerChoice(
      { target: { id: `picker-response-${colIdx}`, value: newParts.join(";") } },
      colIdx
    );
  };

  const handleRemoveDistractor = (colIdx, distractor) => {
    const items = getAllItems(colIdx).filter((v) => v !== distractor);
    props.updatePickerChoice(
      { target: { id: `picker-response-${colIdx}`, value: items.join(";") } },
      colIdx
    );
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId !== destination.droppableId) return;
    if (source.index === destination.index) return;

    const colIdx = parseInt(source.droppableId.replace("picker-col-", ""), 10);
    const items = [...getAllItems(colIdx)];
    const [moved] = items.splice(source.index, 1);
    items.splice(destination.index, 0, moved);

    props.updatePickerChoice(
      {
        target: {
          id: `picker-response-${colIdx}`,
          value: items.filter(Boolean).join(";"),
        },
      },
      colIdx
    );
  };

  return (
    <>
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "16px",
        }}
      >
        <Typography variant="h6" className={props.classes.responseTitle}>
          {t("newEnigma.responses")}
        </Typography>
        <InfoPopover
          title={t("newEnigma.reponseChoixMultiple")}
          text={t("newEnigma.reponseChoixMultipleInfo")}
          src="/images/multiple-choices.jpeg"
        />
      </Box>

      {/* Section 1 — Correct answers */}
      <Typography className={props.classes.pickerSectionTitle}>
        {t("newEnigma.picker_correct_answers_title")}
      </Typography>

      {responsesPicker.map((correctAnswer, colIdx) => (
        <Box key={colIdx} className={props.classes.pickerAnswerRow}>
          <TextField
            label={`${t("newEnigma.picker_column")} ${colIdx + 1}`}
            variant="outlined"
            size="small"
            value={correctAnswer}
            onChange={(e) => handleCorrectAnswerChange(colIdx, e.target.value)}
            style={{ flex: 1 }}
          />
          {colIdx > 0 && (
            <IconButton
              size="small"
              className={props.classes.deleteButton}
              onClick={() => handleRemoveColumn(colIdx)}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      ))}

      <Button
        startIcon={<AddBox />}
        size="small"
        className={props.classes.addButton}
        onClick={handleAddColumn}
        style={{ marginBottom: "24px", marginTop: "4px" }}
      >
        {t("newEnigma.picker_add_column")}
      </Button>

      {/* Section 2 — Sortable choices per column */}
      <Typography className={props.classes.pickerSectionTitle}>
        {t("newEnigma.picker_choices_title")}
      </Typography>

      <DragDropContext onDragEnd={handleDragEnd}>
        {responsesPicker.map((correctAnswer, colIdx) => {
          const allItems = getAllItems(colIdx);
          const isAdding = addingChoiceIdx === colIdx;

          return (
            <Box key={colIdx} className={props.classes.pickerColumnCard}>
              {/* Header */}
              <Box className={props.classes.pickerColumnHeader}>
                <Typography
                  variant="subtitle2"
                  style={{ fontWeight: 700, color: "#1E293B" }}
                >
                  {`${t("newEnigma.picker_column")} ${colIdx + 1}`}
                </Typography>
                {correctAnswer ? (
                  <Chip
                    size="small"
                    label={`✓ ${correctAnswer}`}
                    className={props.classes.pickerCorrectChip}
                  />
                ) : null}
              </Box>

              {/* Draggable list */}
              <Droppable droppableId={`picker-col-${colIdx}`}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      minHeight: "32px",
                      backgroundColor: snapshot.isDraggingOver
                        ? "#F1F5F9"
                        : "transparent",
                      borderRadius: "8px",
                      transition: "background-color 0.15s ease",
                    }}
                  >
                    {allItems.map((item, itemIdx) => {
                      const isCorrect = item === correctAnswer;
                      return (
                        <Draggable
                          key={`picker-${colIdx}-${itemIdx}`}
                          draggableId={`picker-${colIdx}-${itemIdx}`}
                          index={itemIdx}
                        >
                          {(dragProvided, dragSnapshot) => (
                            <Box
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "6px 8px",
                                marginBottom: "4px",
                                borderRadius: "8px",
                                backgroundColor: dragSnapshot.isDragging
                                  ? "#E0F2FE"
                                  : isCorrect
                                    ? "#D1FAE5"
                                    : "#F8FAFC",
                                border: isCorrect
                                  ? "1px solid #6EE7B7"
                                  : "1px solid #E2E8F0",
                                boxShadow: dragSnapshot.isDragging
                                  ? "0 4px 12px rgba(0,0,0,0.15)"
                                  : "none",
                                userSelect: "none",
                                ...dragProvided.draggableProps.style,
                              }}
                            >
                              {/* Drag handle */}
                              <span
                                {...dragProvided.dragHandleProps}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#94A3B8",
                                  cursor: "grab",
                                  flexShrink: 0,
                                }}
                              >
                                <DragIndicatorIcon
                                  style={{ fontSize: "18px" }}
                                />
                              </span>

                              {/* Lock icon for correct answer */}
                              {isCorrect && (
                                <Tooltip
                                  title={t("newEnigma.picker_locked_tooltip")}
                                >
                                  <LockIcon
                                    style={{
                                      fontSize: "14px",
                                      color: "#065F46",
                                      flexShrink: 0,
                                    }}
                                  />
                                </Tooltip>
                              )}

                              {/* Label */}
                              <Typography
                                variant="body2"
                                style={{
                                  flex: 1,
                                  fontWeight: isCorrect ? 600 : 400,
                                  color: isCorrect ? "#065F46" : "#374151",
                                  fontSize: "13px",
                                }}
                              >
                                {item}
                              </Typography>

                              {/* Delete button (distractors only) */}
                              {!isCorrect && (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleRemoveDistractor(colIdx, item)
                                  }
                                  style={{
                                    padding: "2px",
                                    color: "#94A3B8",
                                    flexShrink: 0,
                                  }}
                                >
                                  <CancelIcon style={{ fontSize: "16px" }} />
                                </IconButton>
                              )}
                            </Box>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>

              {/* Add choice */}
              {isAdding ? (
                <TextField
                  autoFocus
                  size="small"
                  variant="outlined"
                  value={newChoiceValue}
                  placeholder={t("newEnigma.picker_new_choice_placeholder")}
                  onChange={(e) => setNewChoiceValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddDistractor(colIdx);
                    if (e.key === "Escape") {
                      setAddingChoiceIdx(null);
                      setNewChoiceValue("");
                    }
                  }}
                  onBlur={() => handleAddDistractor(colIdx)}
                  style={{ marginTop: "8px", width: "200px" }}
                  inputProps={{ maxLength: 100 }}
                />
              ) : (
                <Button
                  startIcon={
                    <AddCircleOutlineIcon style={{ fontSize: "16px" }} />
                  }
                  size="small"
                  onClick={() => {
                    setAddingChoiceIdx(colIdx);
                    setNewChoiceValue("");
                  }}
                  style={{
                    marginTop: "8px",
                    color: "#475569",
                    textTransform: "none",
                  }}
                >
                  {t("newEnigma.picker_add_choice")}
                </Button>
              )}
            </Box>
          );
        })}
      </DragDropContext>
    </>
  );
};

export default PickerResponseComponent;
